import './assets/main.css'
import './assets/css/toasts.css'
import './assets/css/tiptap.css'
import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { useAuthStore } from './stores/auth'
import App from './App.vue'
import router from './router'
import i18n from './i18n/index'
import Toast from 'vue-toastification'
import { Icon } from '@iconify/vue'
import { registerIcons } from './utils/icons'
import { VueQueryPlugin, type VueQueryPluginOptions } from '@tanstack/vue-query'
import { createQueryClient } from './utils/createQueryClient'
import type { Response } from './types/response'
import type { GetUserInfoPayload } from './stores/types'
import { useUserStore } from './stores/userStore'
import { USER_INFO } from './services/apiModules'
import * as Sentry from '@sentry/vue'

const vueQueryOptions: VueQueryPluginOptions = { queryClient: createQueryClient() }

const app = createApp(App)

const getEnvStringItem = <T extends string | undefined>(
  env: unknown,
  defaultValue?: T,
): T extends string ? string : string | undefined => {
  if (typeof env === 'string' && env) {
    return env
  }
  return defaultValue as T extends string ? string : string | undefined
}

const getEnvNumberItem = <T extends number | undefined>(
  env: unknown,
  defaultValue?: number,
): T extends number ? number : number | undefined => {
  if (typeof env === 'string' && env) {
    const value = Number(env)
    if (!isNaN(value)) {
      return value
    }
  }
  return defaultValue as T extends number ? number : number | undefined
}

const sentryDsn = getEnvStringItem(import.meta.env.VITE_SENTRY_DSN) as string | undefined
const sentryDebugEnabled =
  getEnvStringItem(import.meta.env.VITE_SENTRY_ACTIVATE_DEBUG_MODE, 'false')?.toLowerCase() ===
  'true'
const sentryReplayEnabled =
  getEnvStringItem(import.meta.env.VITE_SENTRY_ACTIVATE_REPLAY, 'false')?.toLowerCase() === 'true'

const sentrySampleRate = getEnvNumberItem(
  import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE,
  sentryDebugEnabled ? 1.0 : 0.1,
)

const sentryReplaysSessionSampleRate = getEnvNumberItem(
  import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
  sentryReplayEnabled ? 0.1 : undefined,
)

const sentryReplaysOnErrorSampleRate = getEnvNumberItem(
  import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
  sentryReplayEnabled ? 1.0 : undefined,
)

const sentryConsoleLevels: ('assert' | 'debug' | 'error' | 'info' | 'log' | 'trace' | 'warn')[] =
  sentryDebugEnabled
    ? ['assert', 'debug', 'error', 'info', 'log', 'trace', 'warn']
    : ['warn', 'error']
const sentryBrowserTracingEnabled =
  (import.meta.env.VITE_SENTRY_ACTIVATE_BROWSER_TRACING as string | undefined)?.toLowerCase() ===
  'true'

// Built as one literal: Sentry types each integration by name, so pushing
// other integrations into an array inferred from the first two fails type-checking.
const sentryIntegrations = [
  Sentry.captureConsoleIntegration({
    levels: sentryConsoleLevels,
  }),
  Sentry.consoleLoggingIntegration({
    levels: sentryConsoleLevels,
  }),
  ...(sentryReplayEnabled ? [Sentry.replayIntegration()] : []),
  ...(sentryBrowserTracingEnabled ? [Sentry.browserTracingIntegration()] : []),
]

if (sentryDsn) {
  Sentry.init({
    app,
    dsn: sentryDsn,
    debug: sentryDebugEnabled,
    environment: import.meta.env.MODE,
    // No `enableLogs`: logs are on by default since @sentry/vue 10.71, and v11 removes the option.
    release: `knoten-frontend@${import.meta.env.VITE_APP_VERSION || 'dev'}`,
    integrations: sentryIntegrations,
    tracesSampleRate: sentrySampleRate,
    replaysSessionSampleRate: sentryReplaysSessionSampleRate,
    replaysOnErrorSampleRate: sentryReplaysOnErrorSampleRate,
    // Collects every data category, like the deprecated `sendDefaultPii: true` did.
    dataCollection: {},
  })
}

app.use(VueQueryPlugin, vueQueryOptions)

app.use(i18n)
app.use(createPinia())

app.use(Toast, {
  position: 'bottom-right',
  timeout: 7000,
  closeOnClick: true,
  pauseOnFocusLoss: true,
  pauseOnHover: true,
  draggable: true,
  draggablePercent: 0.6,
  hideProgressBar: false,
  transition: 'Vue-Toastification__fade',
  icon: true,
})

async function initializeApp() {
  const baseUrl = import.meta.env.VITE_API_URL

  const requestData = {
    Key: '',
    Modus: USER_INFO,
    language: 'EN',
    LoginDaten: {},
    Inhalt: {},
  }

  const formData = new FormData()
  formData.append('data', JSON.stringify(requestData))

  try {
    const response = await fetch(baseUrl, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    })

    const data: Response<GetUserInfoPayload> = await response.json()
    const userStore = useUserStore()
    const authStore = useAuthStore()

    if (data.Status === 'ok') {
      console.log('Ping successful:', data)
      const {
        id: userID,
        mail: email,
        Vorname: vorname,
        Nachname: nachname,
        avatar,
        admin,
      } = data.Inhalt
      userStore.registerUser({
        userID: userID !== null ? String(userID) : null,
        email,
        vorname,
        nachname,
        avatar,
        admin,
      })
      authStore.isLoggedIn = true
    } else {
      authStore.piniaLogout()
      console.error('Ping failed:', data)
    }

    // If login was successful, update auth store
  } catch (error) {
    console.error('Ping API request error:', error)
  } finally {
    // Initialize router after ping
    app.use(router)
    // 1. Register component
    app.component('Icon', Icon)

    // 2. Register icons
    await registerIcons()
    // 3. Mount AFTER icons are loaded
    app.mount('#app')

    // After mounting, remove the initial spinner
    const spinner = document.getElementById('initial-spinner')
    spinner?.parentNode?.removeChild(spinner)
  }
}

initializeApp()
